"use client"
import RadarChartPreview from "./RadarChartPreview"

const LandingPage = ({ onNext }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-20 z-0"></div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 animate-fade-in">GTM Assessment</h1>
            <button
              onClick={onNext}
              className="btn-primary text-sm px-4 py-2 rounded-lg font-semibold hover:scale-105 transition-transform duration-200"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Build a Winning
            <span className="text-cyan-600 block animate-pulse">Go-To-Market Strategy</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Unlock a comprehensive evaluation of your GTM strategy across six key dimensions. Visualize your strengths and pinpoint growth opportunities with our intuitive radar analysis.
          </p>
          <button
            onClick={onNext}
            className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            Book a Free Consultation
          </button>
        </div>

        {/* Statistics Section */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">What is Go-To-Market Strategy?</h3>
          <p className="text-gray-600 mb-6">
            According to a recent study by Devry Insights (2025), <strong>84.6%</strong> of companies have already defined a strategy for their Go-To-Market (GTM) approach.
          </p>
          {/* <div className="flex items-center justify-center">
            <div className="relative w-48 h-48">
              <svg className="w-full h-full">
                <circle cx="50%" cy="50%" r="40%" fill="none" stroke="#e0e0e0" strokeWidth="10" />
                <circle
                  cx="50%"
                  cy="50%"
                  r="40%"
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="10"
                  strokeDasharray="251.2"
                  strokeDashoffset="38.69" // 84.6% of 251.2 (circumference)
                  transform="rotate(-90 50 50)"
                />
                <text x="50%" y="50%" textAnchor="middle" dy="0.3em" className="text-3xl font-bold text-gray-900">
                  84.6%
                </text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-4">Source: Devry Insights, 2025</p> */}
          <RadarChartPreview/>
        </div>

        {/* Common Business Challenges */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Business Challenges</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Understand the Market & Customer</h4>
              <p className="text-gray-600">Weak understanding of market & positioning. Targeting based on internal assumptions.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Define Differentiation & Messaging</h4>
              <p className="text-gray-600">Poor differentiation leads to price battles. Not lasting impression of unique strengths.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Clarify Targeting & Positioning</h4>
              <p className="text-gray-600">Lack of focus resources within sales and marketing. No clear path to win on strengths.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Design the Offering & Value Proposition</h4>
              <p className="text-gray-600">Solutions mismatched with customer needs. No messaging to win with value.</p>
            </div>
          </div>
        </div>

        {/* GTM Steps */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-400">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Our GTM Steps</h3>
          <p className="text-gray-600 mb-6">A GTM strategy is your blueprint for building a scalable and repeatable growth engine.</p>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-lg font-semibold">1</span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Who</h4>
                <p className="text-gray-600">Understand the market, customer pain points (market research, customer points).</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-lg font-semibold">2</span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Where</h4>
                <p className="text-gray-600">Clarify targeting and positioning (persona definition, market positioning).</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-lg font-semibold">3</span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">What</h4>
                <p className="text-gray-600">Design the offering and value proposition (what will you deliver and value).</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-lg font-semibold">4</span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Why</h4>
                <p className="text-gray-600">Define differentiation and messaging (why should customers choose you?).</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-lg font-semibold">5</span>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">How</h4>
                <p className="text-gray-600">Structure sales channels & optimize, measure, and execute (how will each channel perform).</p>
              </div>
            </div>
          </div>
          <button
            onClick={onNext}
            className="mt-6 btn-primary text-lg px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            Get a Free Consultation
          </button>
        </div>

        {/* Case Study Section */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-600">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Case Study</h3>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-full md:w-1/3">
              <img
                src="https://via.placeholder.com/200x150"
                alt="Rosetta Stone Case Study"
                className="rounded-lg shadow-md"
              />
            </div>
            <div className="w-full md:w-2/3">
              <h4 className="text-xl font-semibold text-gray-900">Rosetta Stone</h4>
              <p className="text-gray-600">
                Business Challenge: Following the withdrawal of a global partner, the company from its Japan market model. A rapid shift to direct sales and complete overhaul of the sales revenue and organizational instability.
              </p>
            </div>
          </div>
        </div>

        {/* About Us Section */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-800">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">About Us</h3>
          <p className="text-gray-600 mb-4">
            Over the past 20 years, Sales and Marketing Functions have transformed from customer success to diverse and blurred roles—fragmentation has created siloed marketing roles.
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>We don’t just offer strategy.</li>
            <li>We redefine the entire end-to-end.</li>
            <li>Build self-sustaining high-performing organizations.</li>
          </ul>
        </div>

        {/* FAQs Section */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-1000">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">FAQs</h3>
          <div className="space-y-4">
            <details className="group">
              <summary className="text-lg font-semibold text-gray-900 cursor-pointer group-open:mb-2">
                1. What is a Go-To-Market (GTM) Strategy?
              </summary>
              <p className="text-gray-600">A GTM strategy clarifies who to sell to, what to sell, and how to enable a structured approach to revenue generation. It applies to both new product launches and reworking existing marketing efforts.</p>
            </details>
            <details className="group">
              <summary className="text-lg font-semibold text-gray-900 cursor-pointer group-open:mb-2">
                2. What kind of outcomes can we expect?
              </summary>
              <p className="text-gray-600">Expect increased revenue, improved market penetration, and optimized sales processes.</p>
            </details>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-1200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Let’s Collaborate on Your Innovative Ideas</h3>
          <p className="text-gray-600 mb-4">
            Whatever you’re bringing a new product to market, repositioning your brand, or seeking a revenue engine—we’re here to help.
          </p>
          <form className="space-y-4">
            <input
              type="text"
              placeholder="Name"
              className="input-field w-full rounded-md border border-gray-300 p-2"
            />
            <input
              type="email"
              placeholder="Email"
              className="input-field w-full rounded-md border border-gray-300 p-2"
            />
            <textarea
              placeholder="Message"
              className="input-field w-full rounded-md border border-gray-300 p-2 h-24"
            />
            <button
              type="submit"
              className="btn-primary w-full text-lg px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Let’s Talk GTM
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white text-center py-6">
        <p>© GTM Assessment 2025. All Rights Reserved. | Privacy | Terms & Conditions | Sitemap</p>
      </footer>
    </div>
  )
}

export default LandingPage